import { createSelector } from '@reduxjs/toolkit';

import { RootState } from 'types';
import { initialState } from '.';

const selectSlice = (state: RootState) => state.home || initialState;
export const selectReleases = createSelector(
  [selectSlice],
  state => state.releaseData
);
export const selectError = createSelector([selectSlice], state => state.error);
export const selectLoading = createSelector(
  [selectSlice],
  state => state.loading
);

export const selectSilentRefreshing = createSelector(
  [selectSlice],
  state => state.silentRefreshing || false
);

/** True when header should show "Refreshing..." (data refresh or deferred view switching) */
export const selectShowRefreshIndicator = createSelector(
  [selectSlice],
  state =>
    (state.silentRefreshing ||
      state.mustWinViewTransitioning ||
      state.timelineViewTransitioning) ??
    false
);

export const selectLegend = createSelector(
  [selectSlice],
  state => state.legend
);

export const selectFullScreenMode = createSelector(
  [selectSlice],
  state => state.isFullScreenMode
);

export const selectDateRange = createSelector(
  [selectSlice],
  state => state.selectedDates
);
export const selectParkingLot = createSelector(
  [selectSlice],
  state => state.parkingLot
);
export const selectCompact = createSelector(
  [selectSlice],
  state => state.isCompact
);
export const selectMustWinView = createSelector(
  [selectSlice],
  state => state.mustWinViewActive
);

/**
 * Returns true if the card has at least one (audience, strategy) mapping so it would appear in Must Wins view.
 * Mirrors useMustWinSwimLanes: empty object {} or e.g. { "Students": [] } do not show in the view.
 */
function hasAtLeastOneMustWinMapping(mappings: any): boolean {
  if (!mappings) return false;
  const isNewFormat =
    !Array.isArray(mappings) &&
    typeof mappings === 'object' &&
    mappings !== null;
  if (isNewFormat) {
    return Object.values(mappings as Record<string, string[]>).some(
      arr => Array.isArray(arr) && arr.length > 0
    );
  }
  return Array.isArray(mappings) && mappings.length > 0;
}

/** Set of issue IDs and composite keys for all items that appear in Must Wins view (L1/L2/L3 with audienceStrategyMappings, including children and features) */
export const selectMustWinItemIds = createSelector(
  [selectReleases],
  (releases): Set<string> => {
    const ids = new Set<string>();
    const allReleases = releases?.releaseScheduled || [];
    allReleases.forEach((releaseType: any) => {
      if (
        releaseType.type === 'L1' ||
        releaseType.type === 'L2' ||
        releaseType.type === 'L3'
      ) {
        releaseType.projects?.forEach((quarterProject: any) => {
          quarterProject.project?.forEach((card: any) => {
            if (!card.audienceStrategyMappings) return;
            if (!hasAtLeastOneMustWinMapping(card.audienceStrategyMappings))
              return;
            ids.add(card.issueId);
            if (card.releasePhaseId) {
              ids.add(`${card.issueId}-${card.releasePhaseId}`);
              if (card.showOnTimeline !== undefined) {
                ids.add(
                  `${card.issueId}-${card.releasePhaseId}-${card.showOnTimeline}`
                );
              }
            }
            card.childRelationships?.forEach((child: any) => {
              ids.add(child.issueId);
              child.childRelationships?.forEach(
                (grandChild: any) =>
                  grandChild.issueId && ids.add(grandChild.issueId)
              );
            });
            card.features?.forEach(
              (feature: any) =>
                (feature.featureId || feature.issueId) &&
                ids.add(feature.featureId || feature.issueId)
            );
          });
        });
      }
    });
    return ids;
  }
);

export const selectProucts = createSelector(
  [selectSlice],
  state => state.products
);
export const selectProductGroups = createSelector(
  [selectSlice],
  state => state.productGroups
);
export const selectAudiences = createSelector(
  [selectSlice],
  state => state.audiences
);
export const selectCategories = createSelector(
  [selectSlice],
  state => state.categories
);
export const selectEvents = createSelector(
  [selectSlice],
  state => state.events
);

export const selectFeatures = createSelector(
  [selectSlice],
  state => state.features
);

export const selectLanesTiers = createSelector(
  [selectSlice],
  state => state.lanesTiers
);

export const selectStatus = createSelector(
  [selectSlice],
  state => state.status
);

export const selectProductFilter = createSelector(
  [selectSlice],
  state => state.productFilter
);

export const selectCollapseAll = createSelector(
  [selectSlice],
  state => state.collapseAll
);

export const selectUnNestAll = createSelector(
  [selectSlice],
  state => state.unNestAll
);

export const selectUnnestParentIds = createSelector(
  [selectSlice],
  state => state.unnestParentIds
);

export const selectHighlightedIssue = createSelector(
  [selectSlice],
  state => state.highlightedIssue
);

export const selectHighlightSource = createSelector(
  [selectSlice],
  state => state.highlightSource
);

export const selectFilteredReleases = createSelector(
  [selectSlice],
  state => state.filteredReleases || []
);

export const selectKeyDatesRelease = createSelector(
  [selectSlice],
  state => state.keyDatesRelease
);

export const selectSearchFilterActive = createSelector(
  [selectSlice],
  state => state.searchFilterActive
);

export const selectSearchFilteredItems = createSelector(
  [selectSlice],
  state => state.searchFilteredItems
);

export const selectHierarchyFilters = createSelector(
  [selectSlice],
  state => state.hierarchyFilters
);

export const selectAllSurfaces = createSelector(
  [selectHierarchyFilters],
  hierarchy => hierarchy.surfaces || []
);

export const selectCategoryToProductMap = createSelector(
  [selectHierarchyFilters],
  hierarchy => hierarchy.categoryToProductMap || {}
);

export const selectCategoryProductFilter = createSelector(
  [selectSlice],
  state => state.categoryProductFilter
);

export const selectTooltipDetails = createSelector(
  [selectSlice],
  state => state.tooltipDetails
);

export const selectUpdateSuccessAlert = createSelector(
  [selectSlice],
  state => state.updateSuccessAlert
);

export const selectGenAiFilter = createSelector(
  [selectSlice],
  state => state.genAiFilter
);

export const selectRevenueImpactFilter = createSelector(
  [selectSlice],
  state => state.revenueImpactFilter
);

export const selectMomentTypeFilter = createSelector(
  [selectSlice],
  state => state.momentTypeFilter
);

/** RSD moment type list from injected `rsdForms` slice (moment-type filter matching). */
export const selectRsdMomentTypes = (state: RootState) =>
  state.rsdForms?.momentTypes ?? [];

export const selectHideEmptySwimLanes = createSelector(
  [selectSlice],
  state => state.hideEmptySwimLanes
);

export const selectMustWinFilterOptions = createSelector(
  [selectSlice],
  state => state.mustWinFilterOptions
);

export const selectMustWinDropdownSelection = createSelector(
  [selectSlice],
  state => state.mustWinDropdownSelection
);

export const selectSavedViewApplyToken = createSelector(
  [selectSlice],
  state => state.savedViewApplyToken
);

export const selectActiveSavedViewId = createSelector(
  [selectSlice],
  state => state.activeSavedViewId
);

export const selectMyStuffView = createSelector(
  [selectSlice],
  state => state.isMyStuffViewActive
);

export const selectHome = createSelector([selectSlice], state => state);
